import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ChefHat, Heart, Sparkles } from 'lucide-react';

const features = [
  {
    icon: ChefHat,
    title: 'Cocina Auténtica',
    description: 'Recetas tradicionales peruanas preparadas con técnicas ancestrales y los mejores ingredientes.',
  },
  {
    icon: Sparkles,
    title: 'Fusión Oriental',
    description: 'Una propuesta única que combina los sabores del Perú con toques de cocina asiática.',
  },
  {
    icon: Heart,
    title: 'Trato Personalizado',
    description: 'Te recibimos como en casa, con la calidez y hospitalidad que nos caracteriza.',
  },
];

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="nosotros"
      data-testid="about-section"
      ref={ref}
      className="py-20 md:py-32 bg-cream-DEFAULT relative overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-warm-glow opacity-50" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-terracotta/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_salkantay-peru/artifacts/rnzupnnh_salkantay-equipo02.jpg"
                alt="Interior del restaurante SALKANTAY"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                data-testid="about-image"
              />
              {/* Overlapping Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="absolute -bottom-8 -right-4 md:-right-8 bg-white rounded-2xl p-6 shadow-xl max-w-xs"
              >
                <p className="font-accent text-terracotta text-2xl mb-2">Desde 2018</p>
                <p className="text-coffee-light text-sm">
                  Compartiendo los sabores del Perú con Madrid
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="text-terracotta font-medium uppercase tracking-widest text-sm">
              Sobre Nosotros
            </span>
            <h2
              className="font-heading text-4xl md:text-5xl font-bold text-coffee mt-4 mb-6 leading-tight"
              data-testid="about-title"
            >
              Más que un restaurante,
              <br />
              <span className="text-terracotta">un viaje culinario</span>
            </h2>
            <p className="text-coffee-light text-lg leading-relaxed mb-6" data-testid="about-description">
              <strong>SALKANTAY</strong> es más que un restaurante, es un viaje a la auténtica cocina peruana. 
              Desde nuestros tiraditos frescos hasta platos emblemáticos como el ceviche y el chicharrón 
              de pescado, cada receta está hecha con pasión y calidad.
            </p>
            <p className="text-coffee-light text-lg leading-relaxed mb-10">
              Nuestra propuesta única combina las recetas tradicionales del Perú con influencias de la 
              cocina oriental, creando una experiencia gastronómica inolvidable en el corazón de Madrid.
            </p>

            {/* Features */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex gap-4 items-start"
                  data-testid={`about-feature-${index}`}
                >
                  <div className="p-3 bg-terracotta/10 rounded-xl">
                    <feature.icon className="w-6 h-6 text-terracotta" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-coffee text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-coffee-light text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
